/**
 * AW-1 -- campaign filtering and ordering for the Campaign Login gate.
 *
 * WHAT THIS HAS TO SURVIVE
 * ------------------------
 * An agent authorized for roughly 40 state campaigns. The existing
 * searchable Command list is the right interaction for that and is
 * preserved exactly; what it lacked was matching on anything but the
 * campaign NAME, any defined ordering, and any indication of how much
 * the current filter is hiding.
 *
 * WHY THE STATE CODE IS NOT A MAPPING
 * -----------------------------------
 * `GET /telephony/bb6/campaigns` returns exactly
 * `{campaign_id, campaign_name, available_leads}`. There is no state
 * field, and MissionAssist does not own a campaign -> state mapping.
 *
 * So a state code is matched the only honest way available: by matching
 * BOTH fields the provider does return. A state code lives in whichever
 * of them the operator put it in -- "Texas Final Expense" or a
 * `TXFE`-style campaign id -- and searching both finds it either way.
 *
 * The rejected alternative was a built-in 50-state code/name table.
 * That would let "TX" match a campaign named "Texas ..." whose id
 * carries no code, but it invents a mapping this system has never
 * owned, and it would silently mis-file any campaign whose naming did
 * not match the table. A filter that quietly disagrees with the
 * operator's own naming is worse than one that matches exactly what is
 * written.
 *
 * AVAILABLE LEADS IS UNTOUCHED
 * ----------------------------
 * `available_leads` is read for display only -- never to filter, never
 * to order, never recomputed. The known P2-R2 double-count is
 * deliberately out of scope here, and sorting on a figure known to be
 * wrong would have given that defect a new way to mislead.
 */

const SHORT_TERM_MAX_LENGTH = 3;
/** At or below this length a term is treated as a code rather than as
 * free text -- see `filterCampaigns`. */

function nameHasWordStartingWith(name, term) {
  // Word-boundary prefix match. `\b` alone would also fire mid-word for
  // a term beginning with a non-word character, so the boundary is
  // anchored explicitly at the start of the string or after a
  // non-alphanumeric character.
  return name
    .split(/[^a-z0-9]+/)
    .some((word) => word.startsWith(term));
}

/**
 * Campaigns matching `searchTerm`, case-insensitively, against the
 * campaign name and the campaign id.
 *
 * SHORT TERMS ARE TREATED AS CODES
 * --------------------------------
 * A plain substring match is right for free text and wrong for a state
 * code: typing "CA" would surface "Medi(ca)re Supplement", and an agent
 * scanning 40 state campaigns for California does not want Medicare.
 *
 * So a term of three characters or fewer matches only where it reads as
 * a code: anywhere inside the campaign id (ids are short and code-like,
 * so a substring there is already specific), or at the start of a WORD
 * in the campaign name. "CA" then finds `CAFE` and "California Final
 * Expense" but not "Medicare Supplement"; "TX" finds `TXFE`.
 *
 * Longer terms keep plain substring matching on both fields, because
 * "final" should still find "Texas Final Expense" from the middle.
 *
 * An empty or whitespace-only term matches everything. Input order is
 * preserved; ordering is `sortCampaigns`' job, so a caller can filter
 * and sort independently.
 */
export function filterCampaigns(campaigns, searchTerm) {
  const term = (searchTerm ?? "").trim().toLowerCase();
  const list = Array.isArray(campaigns) ? campaigns : [];
  if (!term) return list;
  const asCode = term.length <= SHORT_TERM_MAX_LENGTH;
  return list.filter((campaign) => {
    const name = (campaign?.campaign_name ?? "").toLowerCase();
    const id = (campaign?.campaign_id ?? "").toLowerCase();
    if (id.includes(term)) return true;
    return asCode ? nameHasWordStartingWith(name, term) : name.includes(term);
  });
}

/**
 * A stable, human-meaningful ordering.
 *
 * Primary key is the campaign name, compared with `localeCompare` using
 * numeric collation so "State 2" sorts before "State 10" rather than
 * after it. Ties break on campaign id, so the result is TOTAL: two
 * campaigns can never compare equal unless they are the same campaign,
 * which is what makes the list order identical on every render and
 * every reload.
 *
 * Returns a new array; the caller's array is never mutated, because the
 * campaigns array is React state owned by the gate.
 */
export function sortCampaigns(campaigns) {
  const list = Array.isArray(campaigns) ? [...campaigns] : [];
  return list.sort((left, right) => {
    const byName = (left?.campaign_name ?? "").localeCompare(
      right?.campaign_name ?? "",
      undefined,
      { numeric: true, sensitivity: "base" },
    );
    if (byName !== 0) return byName;
    return (left?.campaign_id ?? "").localeCompare(
      right?.campaign_id ?? "",
      undefined,
      { numeric: true, sensitivity: "base" },
    );
  });
}

/**
 * The count line shown beneath the search box.
 *
 * Reports only the agent's own authorized campaigns. It deliberately
 * carries no figure about other agents -- how many people are dialing a
 * campaign is not an agent-facing number, and this is exactly the kind
 * of summary line where such a number would look natural and be wrong.
 */
export function campaignCountLabel({ filteredCount, totalCount }) {
  if (totalCount === 1 && filteredCount === 1) return "1 campaign";
  if (filteredCount === totalCount) return `${totalCount} campaigns`;
  return `Showing ${filteredCount} of ${totalCount} campaigns`;
}
